import { useEffect, useState, type Dispatch, type FormEvent, type SetStateAction } from 'react';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import { useNavigate } from 'react-router-dom';
import type {
  AdultModelApplicationRecord,
  ChildModelApplicationRecord,
  EventTicketType,
  PublicEvent,
} from '../types';
import { calculateCustomerFacingProcessingFeeCents, calculateCustomerFacingTotalCents, formatUsdCents } from '../lib/paymentFees';
import { submitPublicAdultModelApplication } from '../services/adult-model-applications';
import { getPreviewBase } from '../services/bliss-app-auth';
import { submitPublicChildModelApplication } from '../services/child-model-applications';
import {
  createEventTicketIntent,
  getEventTicketOrderStatus,
  getSitePaymentConfig,
  type EventTicketIntentResponse,
} from '../services/event-payments';

const MAX_TICKET_QUANTITY = 25;
const ORDER_STATUS_RECOVERY_ATTEMPTS = 3;
const ORDER_STATUS_RECOVERY_DELAY_MS = 1200;

function delay(ms: number) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function isVendorTicketCategory(category: string) {
  const normalized = String(category || '').trim().toLowerCase();
  return normalized === 'vendor' || normalized === 'resource-vendor';
}

function isAdultModelTicket(ticket: EventTicketType) {
  return ticket.category === 'participant' && ticket.participantType === 'adult-model';
}

function isChildModelTicket(ticket: EventTicketType) {
  return ticket.category === 'participant' && ticket.participantType === 'child-model';
}

type EditableVendorRegistration = {
  profile_type: 'vendor' | 'resource_vendor';
  business_name: string;
  contact_name: string;
  email: string;
  phone: string;
  website_url: string;
  instagram_handle: string;
  description: string;
  booth_details: string;
};

type EditableAdultModelApplication = {
  display_name: string;
  email: string;
  phone: string;
  birthday: string;
  city: string;
  state: string;
  height: string;
  instagram_handle: string;
  experience_summary: string;
  application_message: string;
  availability_details: string;
};

type EditableChildModelApplication = {
  child_first_name: string;
  child_last_name: string;
  child_birthday: string;
  guardian_name: string;
  guardian_email: string;
  guardian_phone: string;
  guardian_relationship: string;
  city: string;
  state: string;
  instagram_handle: string;
  experience_summary: string;
  allergies_or_accommodations: string;
  application_message: string;
  availability_details: string;
  participation_consent: boolean;
  media_release_approved: boolean;
};

type TicketPaymentCompletion = {
  status: 'processing' | 'succeeded';
  paymentIntentId: string | null;
};

function createDefaultVendorRegistration(ticket: EventTicketType): EditableVendorRegistration {
  return {
    profile_type: ticket.category === 'resource-vendor' ? 'resource_vendor' : 'vendor',
    business_name: '',
    contact_name: '',
    email: '',
    phone: '',
    website_url: '',
    instagram_handle: '',
    description: '',
    booth_details: '',
  };
}

function createDefaultAdultModelApplication(): EditableAdultModelApplication {
  return {
    display_name: '',
    email: '',
    phone: '',
    birthday: '',
    city: '',
    state: '',
    height: '',
    instagram_handle: '',
    experience_summary: '',
    application_message: '',
    availability_details: '',
  };
}

function createDefaultChildModelApplication(): EditableChildModelApplication {
  return {
    child_first_name: '',
    child_last_name: '',
    child_birthday: '',
    guardian_name: '',
    guardian_email: '',
    guardian_phone: '',
    guardian_relationship: '',
    city: '',
    state: '',
    instagram_handle: '',
    experience_summary: '',
    allergies_or_accommodations: '',
    application_message: '',
    availability_details: '',
    participation_consent: false,
    media_release_approved: false,
  };
}

function isVendorRegistrationComplete(registration: EditableVendorRegistration | null) {
  if (!registration) return false;
  return Boolean(
    registration.business_name.trim() &&
      registration.contact_name.trim() &&
      registration.email.trim() &&
      registration.phone.trim(),
  );
}

function isAdultModelApplicationComplete(application: EditableAdultModelApplication | null) {
  if (!application) return false;
  return Boolean(
    application.display_name.trim() &&
      application.email.trim() &&
      application.phone.trim() &&
      application.birthday.trim() &&
      application.city.trim() &&
      application.state.trim(),
  );
}

function isChildModelApplicationComplete(application: EditableChildModelApplication | null) {
  if (!application) return false;
  return Boolean(
    application.child_first_name.trim() &&
      application.child_last_name.trim() &&
      application.child_birthday.trim() &&
      application.guardian_name.trim() &&
      application.guardian_email.trim() &&
      application.guardian_phone.trim() &&
      application.guardian_relationship.trim() &&
      application.city.trim() &&
      application.state.trim() &&
      application.participation_consent,
  );
}

function sanitizeTicketQuantity(value: number): number {
  if (!Number.isFinite(value)) {
    return 1;
  }

  return Math.min(MAX_TICKET_QUANTITY, Math.max(1, Math.round(value)));
}

function EventTicketPaymentForm({
  event,
  ticket,
  purchaserName,
  purchaserEmail,
  totalLabel,
  orderId,
  clientSecret,
  onSuccess,
}: {
  event: PublicEvent;
  ticket: EventTicketType;
  purchaserName: string;
  purchaserEmail: string;
  totalLabel: string;
  orderId: string;
  clientSecret: string;
  onSuccess: (input: TicketPaymentCompletion) => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [paymentError, setPaymentError] = useState('');

  function applyRecoveredStatus(status: string, paymentIntentId: string | null = null) {
    if (status === 'succeeded' || status === 'paid') {
      onSuccess({
        status: 'succeeded',
        paymentIntentId,
      });
      return true;
    }

    if (status === 'processing') {
      onSuccess({
        status: 'processing',
        paymentIntentId,
      });
      return true;
    }

    return false;
  }

  async function resolvePaymentStateFallback() {
    if (!stripe) {
      return false;
    }

    const retrieved = await stripe.retrievePaymentIntent(clientSecret);
    if (applyRecoveredStatus(retrieved.paymentIntent?.status || '', retrieved.paymentIntent?.id ?? null)) {
      return true;
    }

    if (!orderId) {
      return false;
    }

    for (let attempt = 0; attempt < ORDER_STATUS_RECOVERY_ATTEMPTS; attempt += 1) {
      if (attempt > 0) {
        await delay(ORDER_STATUS_RECOVERY_DELAY_MS);
      }

      const response = await getEventTicketOrderStatus({ orderId });
      if (applyRecoveredStatus(response.order.payment_status || '', response.order.stripe_payment_intent_id ?? null)) {
        return true;
      }
    }

    return false;
  }

  async function handleSubmit(eventInput: FormEvent<HTMLFormElement>) {
    eventInput.preventDefault();
    if (!stripe || !elements) return;

    setIsSubmitting(true);
    setPaymentError('');

    try {
      const previewBase = getPreviewBase() || '';
      const returnUrl = `${window.location.origin}${previewBase}/events/${event.slug}/thank-you?order_id=${encodeURIComponent(orderId)}&ticket_id=${encodeURIComponent(ticket.id)}`;
      const result = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: returnUrl,
          payment_method_data: {
            billing_details: {
              ...(purchaserName ? { name: purchaserName } : {}),
              ...(purchaserEmail ? { email: purchaserEmail } : {}),
            },
          },
        },
        redirect: 'if_required',
      });

      if (result.error) {
        const recovered = await resolvePaymentStateFallback();
        if (recovered) {
          return;
        }

        setPaymentError(result.error.message || 'Payment failed. Please try again.');
        return;
      }

      const status = result.paymentIntent?.status;
      if (applyRecoveredStatus(status || '', result.paymentIntent?.id ?? null)) {
        return;
      }

      const recovered = await resolvePaymentStateFallback();
      if (recovered) {
        return;
      }

      setPaymentError('Payment was not completed. Please try again.');
    } catch (error) {
      try {
        const recovered = await resolvePaymentStateFallback();
        if (recovered) {
          return;
        }
      } catch {
        // Keep the original caught error if the Stripe fallback check also fails.
      }

      setPaymentError(error instanceof Error ? error.message : 'Payment failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="event-checkout-payment-form">
      <PaymentElement />
      {paymentError ? <div className="auth-error">{paymentError}</div> : null}
      <button type="submit" className="button-primary tap-target event-checkout-submit" disabled={!stripe || !elements || isSubmitting}>
        {isSubmitting ? 'Processing payment...' : `Pay ${totalLabel}`}
      </button>
    </form>
  );
}

type VendorSectionProps = {
  event: PublicEvent;
  ticket: EventTicketType;
  ticketQuantity: number;
  totalCents: number;
  isPreparing: boolean;
  checkoutError: string;
  clientSecret: string | null;
  stripePromise: Promise<Stripe | null> | null;
  intentSummary: EventTicketIntentResponse | null;
  vendorRegistration: EditableVendorRegistration | null;
  vendorRegistrationReady: boolean;
  setVendorRegistration: Dispatch<SetStateAction<EditableVendorRegistration | null>>;
  setTicketQuantity: Dispatch<SetStateAction<number>>;
  handlePrepareCheckout: () => Promise<void>;
  handlePaymentComplete: (input: TicketPaymentCompletion) => void;
};

function VendorCheckoutSection({
  event,
  ticket,
  ticketQuantity,
  totalCents,
  isPreparing,
  checkoutError,
  clientSecret,
  stripePromise,
  intentSummary,
  vendorRegistration,
  vendorRegistrationReady,
  setVendorRegistration,
  setTicketQuantity,
  handlePrepareCheckout,
  handlePaymentComplete,
}: VendorSectionProps) {
  return (
    <>
      <div className="event-checkout-gate event-checkout-gate--vendor">
        <p className="info-kicker">Vendor registration</p>
        <h4>Fill out the vendor form, then continue to payment.</h4>
        <p>The registration details will show up with the paid order in the portal dashboard.</p>

        {vendorRegistration ? (
          <div className="auth-form-grid event-vendor-profile-form">
            <label className="auth-field">
              <span>Quantity</span>
              <input
                className="auth-input"
                type="number"
                min={1}
                max={MAX_TICKET_QUANTITY}
                step={1}
                value={ticketQuantity}
                onChange={(eventInput) => setTicketQuantity(sanitizeTicketQuantity(Number(eventInput.target.value)))}
                required
              />
            </label>
            <label className="auth-field">
              <span>Business or organization name</span>
              <input
                className="auth-input"
                value={vendorRegistration.business_name}
                onChange={(eventInput) =>
                  setVendorRegistration((current) => current ? { ...current, business_name: eventInput.target.value } : current)
                }
                required
              />
            </label>
            <label className="auth-field">
              <span>Contact name</span>
              <input
                className="auth-input"
                value={vendorRegistration.contact_name}
                onChange={(eventInput) =>
                  setVendorRegistration((current) => current ? { ...current, contact_name: eventInput.target.value } : current)
                }
                required
              />
            </label>
            <label className="auth-field">
              <span>Email for receipt</span>
              <input
                className="auth-input"
                type="email"
                value={vendorRegistration.email}
                onChange={(eventInput) =>
                  setVendorRegistration((current) => current ? { ...current, email: eventInput.target.value } : current)
                }
                required
              />
            </label>
            <label className="auth-field">
              <span>Phone</span>
              <input
                className="auth-input"
                value={vendorRegistration.phone}
                onChange={(eventInput) =>
                  setVendorRegistration((current) => current ? { ...current, phone: eventInput.target.value } : current)
                }
                required
              />
            </label>
            <label className="auth-field">
              <span>Website</span>
              <input
                className="auth-input"
                value={vendorRegistration.website_url}
                onChange={(eventInput) =>
                  setVendorRegistration((current) => current ? { ...current, website_url: eventInput.target.value } : current)
                }
                placeholder="https://..."
              />
            </label>
            <label className="auth-field">
              <span>Instagram or social handle</span>
              <input
                className="auth-input"
                value={vendorRegistration.instagram_handle}
                onChange={(eventInput) =>
                  setVendorRegistration((current) => current ? { ...current, instagram_handle: eventInput.target.value } : current)
                }
                placeholder="@yourbrand"
              />
            </label>
            <label className="auth-field auth-field--full">
              <span>What do you offer?</span>
              <textarea
                className="auth-input auth-textarea"
                value={vendorRegistration.description}
                onChange={(eventInput) =>
                  setVendorRegistration((current) => current ? { ...current, description: eventInput.target.value } : current)
                }
                rows={4}
              />
            </label>
            <label className="auth-field auth-field--full">
              <span>Booth notes or setup needs</span>
              <textarea
                className="auth-input auth-textarea"
                value={vendorRegistration.booth_details}
                onChange={(eventInput) =>
                  setVendorRegistration((current) => current ? { ...current, booth_details: eventInput.target.value } : current)
                }
                rows={3}
              />
            </label>
          </div>
        ) : null}
      </div>

      {checkoutError ? <div className="auth-error">{checkoutError}</div> : null}

      {!clientSecret || !stripePromise ? (
        <button
          type="button"
          className="button-primary tap-target event-checkout-submit"
          onClick={() => void handlePrepareCheckout()}
          disabled={isPreparing || !vendorRegistrationReady}
        >
          {isPreparing ? 'Preparing secure payment...' : `Continue to payment (${formatUsdCents(totalCents)})`}
        </button>
      ) : null}

      {stripePromise && clientSecret ? (
        <Elements
          stripe={stripePromise}
          options={{
            clientSecret,
            appearance: { theme: 'stripe' },
          }}
        >
          <EventTicketPaymentForm
            event={event}
            ticket={ticket}
            purchaserName={vendorRegistration?.contact_name.trim() || ''}
            purchaserEmail={vendorRegistration?.email.trim() || ''}
            totalLabel={formatUsdCents(intentSummary?.amount_cents ?? totalCents)}
            orderId={intentSummary?.order_id ?? ''}
            clientSecret={clientSecret}
            onSuccess={handlePaymentComplete}
          />
        </Elements>
      ) : null}
    </>
  );
}

type AdultSectionProps = {
  adultModelForm: EditableAdultModelApplication | null;
  adultModelApplication: AdultModelApplicationRecord | null;
  adultModelError: string;
  adultModelSuccess: string;
  isSubmittingAdultModel: boolean;
  adultModelReady: boolean;
  setAdultModelForm: Dispatch<SetStateAction<EditableAdultModelApplication | null>>;
  handleSubmitAdultModelApplication: (eventInput: FormEvent<HTMLFormElement>) => Promise<void>;
};

function AdultModelSection({
  adultModelForm,
  adultModelApplication,
  adultModelError,
  adultModelSuccess,
  isSubmittingAdultModel,
  adultModelReady,
  setAdultModelForm,
  handleSubmitAdultModelApplication,
}: AdultSectionProps) {
  return (
    <div className="event-checkout-gate event-checkout-gate--vendor">
      <p className="info-kicker">Adult model application</p>
      <h4>Submit your registration from this page.</h4>
      <p>Victoria&apos;s Place will review the submitted information in the portal dashboard.</p>

      {adultModelApplication ? (
        <div className="auth-runtime-note">
          <p className="info-kicker">Current status</p>
          <p>Your application is currently marked {adultModelApplication.status.replace('-', ' ')}.</p>
          {adultModelApplication.review_notes ? <p>{adultModelApplication.review_notes}</p> : null}
        </div>
      ) : null}

      {adultModelForm ? (
        <form className="auth-form-grid event-vendor-profile-form" onSubmit={(eventInput) => void handleSubmitAdultModelApplication(eventInput)}>
          <label className="auth-field">
            <span>Model name</span>
            <input
              className="auth-input"
              value={adultModelForm.display_name}
              onChange={(eventInput) =>
                setAdultModelForm((current) => current ? { ...current, display_name: eventInput.target.value } : current)
              }
              required
            />
          </label>
          <label className="auth-field">
            <span>Birthday (MM/DD/YYYY)</span>
            <input
              className="auth-input"
              value={adultModelForm.birthday}
              onChange={(eventInput) =>
                setAdultModelForm((current) => current ? { ...current, birthday: eventInput.target.value } : current)
              }
              placeholder="MM/DD/YYYY"
              required
            />
          </label>
          <label className="auth-field">
            <span>Email</span>
            <input
              className="auth-input"
              type="email"
              value={adultModelForm.email}
              onChange={(eventInput) =>
                setAdultModelForm((current) => current ? { ...current, email: eventInput.target.value } : current)
              }
              required
            />
          </label>
          <label className="auth-field">
            <span>Phone number</span>
            <input
              className="auth-input"
              value={adultModelForm.phone}
              onChange={(eventInput) =>
                setAdultModelForm((current) => current ? { ...current, phone: eventInput.target.value } : current)
              }
              required
            />
          </label>
          <label className="auth-field">
            <span>City</span>
            <input
              className="auth-input"
              value={adultModelForm.city}
              onChange={(eventInput) =>
                setAdultModelForm((current) => current ? { ...current, city: eventInput.target.value } : current)
              }
              required
            />
          </label>
          <label className="auth-field">
            <span>State</span>
            <input
              className="auth-input"
              value={adultModelForm.state}
              onChange={(eventInput) =>
                setAdultModelForm((current) => current ? { ...current, state: eventInput.target.value } : current)
              }
              required
            />
          </label>
          <label className="auth-field">
            <span>Instagram or social handle</span>
            <input
              className="auth-input"
              value={adultModelForm.instagram_handle}
              onChange={(eventInput) =>
                setAdultModelForm((current) => current ? { ...current, instagram_handle: eventInput.target.value } : current)
              }
              placeholder="@yourhandle"
            />
          </label>
          <label className="auth-field">
            <span>Height</span>
            <input
              className="auth-input"
              value={adultModelForm.height}
              onChange={(eventInput) =>
                setAdultModelForm((current) => current ? { ...current, height: eventInput.target.value } : current)
              }
              placeholder="Example: 5'9&quot;"
            />
          </label>
          <label className="auth-field auth-field--full">
            <span>Experience summary</span>
            <textarea
              className="auth-input auth-textarea"
              value={adultModelForm.experience_summary}
              onChange={(eventInput) =>
                setAdultModelForm((current) => current ? { ...current, experience_summary: eventInput.target.value } : current)
              }
              rows={4}
            />
          </label>
          <label className="auth-field auth-field--full">
            <span>Why do you want to be part of this event?</span>
            <textarea
              className="auth-input auth-textarea"
              value={adultModelForm.application_message}
              onChange={(eventInput) =>
                setAdultModelForm((current) => current ? { ...current, application_message: eventInput.target.value } : current)
              }
              rows={4}
            />
          </label>
          <label className="auth-field auth-field--full">
            <span>Availability or scheduling notes</span>
            <textarea
              className="auth-input auth-textarea"
              value={adultModelForm.availability_details}
              onChange={(eventInput) =>
                setAdultModelForm((current) => current ? { ...current, availability_details: eventInput.target.value } : current)
              }
              rows={3}
            />
          </label>

          {adultModelError ? <div className="auth-error">{adultModelError}</div> : null}
          {adultModelSuccess ? <div className="auth-success">{adultModelSuccess}</div> : null}

          <button
            type="submit"
            className="button-primary tap-target event-checkout-submit"
            disabled={isSubmittingAdultModel || !adultModelReady}
          >
            {isSubmittingAdultModel
              ? 'Submitting application...'
              : adultModelApplication
                ? 'Update application'
                : 'Submit adult model application'}
          </button>
        </form>
      ) : null}
    </div>
  );
}

type ChildSectionProps = {
  childModelForm: EditableChildModelApplication | null;
  childModelApplication: ChildModelApplicationRecord | null;
  childModelError: string;
  childModelSuccess: string;
  isSubmittingChildModel: boolean;
  childModelReady: boolean;
  setChildModelForm: Dispatch<SetStateAction<EditableChildModelApplication | null>>;
  handleSubmitChildModelApplication: (eventInput: FormEvent<HTMLFormElement>) => Promise<void>;
};

function ChildModelSection({
  childModelForm,
  childModelApplication,
  childModelError,
  childModelSuccess,
  isSubmittingChildModel,
  childModelReady,
  setChildModelForm,
  handleSubmitChildModelApplication,
}: ChildSectionProps) {
  return (
    <div className="event-checkout-gate event-checkout-gate--vendor">
      <p className="info-kicker">Child model application</p>
      <h4>Submit the child registration from this page.</h4>
      <p>Victoria&apos;s Place will review the submitted information in the portal dashboard.</p>

      {childModelApplication ? (
        <div className="auth-runtime-note">
          <p className="info-kicker">Current status</p>
          <p>Your application is currently marked {childModelApplication.status.replace('-', ' ')}.</p>
          {childModelApplication.review_notes ? <p>{childModelApplication.review_notes}</p> : null}
        </div>
      ) : null}

      {childModelForm ? (
        <form className="auth-form-grid event-vendor-profile-form" onSubmit={(eventInput) => void handleSubmitChildModelApplication(eventInput)}>
          <label className="auth-field">
            <span>Child first name</span>
            <input
              className="auth-input"
              value={childModelForm.child_first_name}
              onChange={(eventInput) =>
                setChildModelForm((current) => current ? { ...current, child_first_name: eventInput.target.value } : current)
              }
              required
            />
          </label>
          <label className="auth-field">
            <span>Child last name</span>
            <input
              className="auth-input"
              value={childModelForm.child_last_name}
              onChange={(eventInput) =>
                setChildModelForm((current) => current ? { ...current, child_last_name: eventInput.target.value } : current)
              }
              required
            />
          </label>
          <label className="auth-field">
            <span>Birthday (MM/DD/YYYY)</span>
            <input
              className="auth-input"
              value={childModelForm.child_birthday}
              onChange={(eventInput) =>
                setChildModelForm((current) => current ? { ...current, child_birthday: eventInput.target.value } : current)
              }
              placeholder="MM/DD/YYYY"
              required
            />
          </label>
          <label className="auth-field">
            <span>Parent or guardian name</span>
            <input
              className="auth-input"
              value={childModelForm.guardian_name}
              onChange={(eventInput) =>
                setChildModelForm((current) => current ? { ...current, guardian_name: eventInput.target.value } : current)
              }
              required
            />
          </label>
          <label className="auth-field">
            <span>Guardian email</span>
            <input
              className="auth-input"
              type="email"
              value={childModelForm.guardian_email}
              onChange={(eventInput) =>
                setChildModelForm((current) => current ? { ...current, guardian_email: eventInput.target.value } : current)
              }
              required
            />
          </label>
          <label className="auth-field">
            <span>Guardian phone number</span>
            <input
              className="auth-input"
              value={childModelForm.guardian_phone}
              onChange={(eventInput) =>
                setChildModelForm((current) => current ? { ...current, guardian_phone: eventInput.target.value } : current)
              }
              required
            />
          </label>
          <label className="auth-field">
            <span>Relationship to child</span>
            <input
              className="auth-input"
              value={childModelForm.guardian_relationship}
              onChange={(eventInput) =>
                setChildModelForm((current) => current ? { ...current, guardian_relationship: eventInput.target.value } : current)
              }
              placeholder="Parent, guardian, grandparent..."
              required
            />
          </label>
          <label className="auth-field">
            <span>City</span>
            <input
              className="auth-input"
              value={childModelForm.city}
              onChange={(eventInput) =>
                setChildModelForm((current) => current ? { ...current, city: eventInput.target.value } : current)
              }
              required
            />
          </label>
          <label className="auth-field">
            <span>State</span>
            <input
              className="auth-input"
              value={childModelForm.state}
              onChange={(eventInput) =>
                setChildModelForm((current) => current ? { ...current, state: eventInput.target.value } : current)
              }
              required
            />
          </label>
          <label className="auth-field">
            <span>Instagram or social handle</span>
            <input
              className="auth-input"
              value={childModelForm.instagram_handle}
              onChange={(eventInput) =>
                setChildModelForm((current) => current ? { ...current, instagram_handle: eventInput.target.value } : current)
              }
              placeholder="@guardianhandle or @childhandle"
            />
          </label>
          <label className="auth-field auth-field--full">
            <span>Experience summary</span>
            <textarea
              className="auth-input auth-textarea"
              value={childModelForm.experience_summary}
              onChange={(eventInput) =>
                setChildModelForm((current) => current ? { ...current, experience_summary: eventInput.target.value } : current)
              }
              rows={4}
            />
          </label>
          <label className="auth-field auth-field--full">
            <span>Allergies or accommodations</span>
            <textarea
              className="auth-input auth-textarea"
              value={childModelForm.allergies_or_accommodations}
              onChange={(eventInput) =>
                setChildModelForm((current) => current ? { ...current, allergies_or_accommodations: eventInput.target.value } : current)
              }
              rows={3}
            />
          </label>
          <label className="auth-field auth-field--full">
            <span>Why is this child interested in participating?</span>
            <textarea
              className="auth-input auth-textarea"
              value={childModelForm.application_message}
              onChange={(eventInput) =>
                setChildModelForm((current) => current ? { ...current, application_message: eventInput.target.value } : current)
              }
              rows={4}
            />
          </label>
          <label className="auth-field auth-field--full">
            <span>Availability or scheduling notes</span>
            <textarea
              className="auth-input auth-textarea"
              value={childModelForm.availability_details}
              onChange={(eventInput) =>
                setChildModelForm((current) => current ? { ...current, availability_details: eventInput.target.value } : current)
              }
              rows={3}
            />
          </label>
          <label className="auth-field auth-field--full">
            <span className="sr-only">Participation consent</span>
            <span className="event-checkout-note">
              <input
                type="checkbox"
                checked={childModelForm.participation_consent}
                onChange={(eventInput) =>
                  setChildModelForm((current) => current ? { ...current, participation_consent: eventInput.target.checked } : current)
                }
              />{' '}
              I confirm that I am the parent or legal guardian and consent to this child participating.
            </span>
          </label>
          <label className="auth-field auth-field--full">
            <span className="sr-only">Media release approval</span>
            <span className="event-checkout-note">
              <input
                type="checkbox"
                checked={childModelForm.media_release_approved}
                onChange={(eventInput) =>
                  setChildModelForm((current) => current ? { ...current, media_release_approved: eventInput.target.checked } : current)
                }
              />{' '}
              I approve event photography and media use for this child model registration.
            </span>
          </label>

          {childModelError ? <div className="auth-error">{childModelError}</div> : null}
          {childModelSuccess ? <div className="auth-success">{childModelSuccess}</div> : null}

          <button
            type="submit"
            className="button-primary tap-target event-checkout-submit"
            disabled={isSubmittingChildModel || !childModelReady}
          >
            {isSubmittingChildModel
              ? 'Submitting application...'
              : childModelApplication
                ? 'Update application'
                : 'Submit child model application'}
          </button>
        </form>
      ) : null}
    </div>
  );
}

type GeneralSectionProps = {
  event: PublicEvent;
  ticket: EventTicketType;
  ticketQuantity: number;
  totalCents: number;
  purchaserName: string;
  purchaserEmail: string;
  isPreparing: boolean;
  checkoutError: string;
  clientSecret: string | null;
  stripePromise: Promise<Stripe | null> | null;
  intentSummary: EventTicketIntentResponse | null;
  setPurchaserName: Dispatch<SetStateAction<string>>;
  setPurchaserEmail: Dispatch<SetStateAction<string>>;
  setTicketQuantity: Dispatch<SetStateAction<number>>;
  handlePrepareCheckout: () => Promise<void>;
  handlePaymentComplete: (input: TicketPaymentCompletion) => void;
};

function GeneralTicketSection({
  event,
  ticket,
  ticketQuantity,
  totalCents,
  purchaserName,
  purchaserEmail,
  isPreparing,
  checkoutError,
  clientSecret,
  stripePromise,
  intentSummary,
  setPurchaserName,
  setPurchaserEmail,
  setTicketQuantity,
  handlePrepareCheckout,
  handlePaymentComplete,
}: GeneralSectionProps) {
  return (
    <>
      <div className="event-checkout-form-grid">
        <label className="auth-field">
          <span>Quantity</span>
          <input
            className="auth-input"
            type="number"
            min={1}
            max={MAX_TICKET_QUANTITY}
            step={1}
            value={ticketQuantity}
            onChange={(eventInput) => setTicketQuantity(sanitizeTicketQuantity(Number(eventInput.target.value)))}
            required
          />
        </label>
        <label className="auth-field">
          <span>Name on ticket</span>
          <input
            className="auth-input"
            value={purchaserName}
            onChange={(eventInput) => setPurchaserName(eventInput.target.value)}
            placeholder="Your name"
            autoComplete="name"
          />
        </label>
        <label className="auth-field">
          <span>Email for receipt</span>
          <input
            className="auth-input"
            value={purchaserEmail}
            onChange={(eventInput) => setPurchaserEmail(eventInput.target.value)}
            placeholder="you@example.com"
            autoComplete="email"
            type="email"
            required
          />
        </label>
      </div>

      {checkoutError ? <div className="auth-error">{checkoutError}</div> : null}

      {!clientSecret || !stripePromise ? (
        <button
          type="button"
          className="button-primary tap-target event-checkout-submit"
          onClick={() => void handlePrepareCheckout()}
          disabled={isPreparing}
        >
          {isPreparing ? 'Preparing secure payment...' : `Continue to payment (${formatUsdCents(totalCents)})`}
        </button>
      ) : null}

      {stripePromise && clientSecret ? (
        <Elements
          stripe={stripePromise}
          options={{
            clientSecret,
            appearance: { theme: 'stripe' },
          }}
        >
          <EventTicketPaymentForm
            event={event}
            ticket={ticket}
            purchaserName={purchaserName.trim()}
            purchaserEmail={purchaserEmail.trim()}
            totalLabel={formatUsdCents(intentSummary?.amount_cents ?? totalCents)}
            orderId={intentSummary?.order_id ?? ''}
            clientSecret={clientSecret}
            onSuccess={handlePaymentComplete}
          />
        </Elements>
      ) : null}
    </>
  );
}

export default function EventTicketCheckout({
  event,
  ticket,
}: {
  event: PublicEvent;
  ticket: EventTicketType;
}) {
  const navigate = useNavigate();
  const [purchaserName, setPurchaserName] = useState('');
  const [purchaserEmail, setPurchaserEmail] = useState('');
  const [ticketQuantity, setTicketQuantity] = useState(1);
  const [isPreparing, setIsPreparing] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [intentSummary, setIntentSummary] = useState<EventTicketIntentResponse | null>(null);
  const [vendorRegistration, setVendorRegistration] = useState<EditableVendorRegistration | null>(null);
  const [adultModelForm, setAdultModelForm] = useState<EditableAdultModelApplication | null>(null);
  const [adultModelApplication, setAdultModelApplication] = useState<AdultModelApplicationRecord | null>(null);
  const [adultModelError, setAdultModelError] = useState('');
  const [adultModelSuccess, setAdultModelSuccess] = useState('');
  const [isSubmittingAdultModel, setIsSubmittingAdultModel] = useState(false);
  const [childModelForm, setChildModelForm] = useState<EditableChildModelApplication | null>(null);
  const [childModelApplication, setChildModelApplication] = useState<ChildModelApplicationRecord | null>(null);
  const [childModelError, setChildModelError] = useState('');
  const [childModelSuccess, setChildModelSuccess] = useState('');
  const [isSubmittingChildModel, setIsSubmittingChildModel] = useState(false);

  const unitAmountCents = Math.round((ticket.price ?? 0) * 100);
  const baseAmountCents = unitAmountCents * ticketQuantity;
  const customerFeeCents = calculateCustomerFacingProcessingFeeCents(baseAmountCents);
  const totalCents = calculateCustomerFacingTotalCents(baseAmountCents);
  const vendorTicket = isVendorTicketCategory(ticket.category);
  const adultModelTicket = isAdultModelTicket(ticket);
  const childModelTicket = isChildModelTicket(ticket);
  const vendorRegistrationReady = !vendorTicket || isVendorRegistrationComplete(vendorRegistration);
  const adultModelReady = !adultModelTicket || isAdultModelApplicationComplete(adultModelForm);
  const childModelReady = !childModelTicket || isChildModelApplicationComplete(childModelForm);

  useEffect(() => {
    setPurchaserName('');
    setPurchaserEmail('');
    setTicketQuantity(1);
    setIsPreparing(false);
    setCheckoutError('');
    setStripePromise(null);
    setClientSecret(null);
    setIntentSummary(null);
    setVendorRegistration(vendorTicket ? createDefaultVendorRegistration(ticket) : null);
    setAdultModelForm(adultModelTicket ? createDefaultAdultModelApplication() : null);
    setAdultModelApplication(null);
    setAdultModelError('');
    setAdultModelSuccess('');
    setChildModelForm(childModelTicket ? createDefaultChildModelApplication() : null);
    setChildModelApplication(null);
    setChildModelError('');
    setChildModelSuccess('');
  }, [adultModelTicket, childModelTicket, ticket, vendorTicket]);

  useEffect(() => {
    setIsPreparing(false);
    setCheckoutError('');
    setStripePromise(null);
    setClientSecret(null);
    setIntentSummary(null);
  }, [ticketQuantity]);

  function handleTicketPaymentComplete(input: TicketPaymentCompletion) {
    const nextParams = new URLSearchParams();
    nextParams.set('status', input.status);
    nextParams.set('order_id', intentSummary?.order_id ?? '');
    if (input.paymentIntentId) {
      nextParams.set('payment_intent', input.paymentIntentId);
    }

    navigate({
      pathname: `/events/${event.slug}/thank-you`,
      search: nextParams.toString(),
    });
  }

  async function handlePrepareCheckout() {
    if (adultModelTicket || childModelTicket) {
      setCheckoutError(
        adultModelTicket
          ? 'Adult model registration is submitted as an application and does not require payment.'
          : 'Child model registration is submitted as an application and does not require payment.',
      );
      return;
    }

    if (vendorTicket && !vendorRegistrationReady) {
      setCheckoutError('Complete the vendor registration form before moving to payment.');
      return;
    }

    const effectivePurchaserName = vendorTicket ? vendorRegistration?.contact_name.trim() || '' : purchaserName.trim();
    const effectivePurchaserEmail = vendorTicket ? vendorRegistration?.email.trim() || '' : purchaserEmail.trim();
    if (!effectivePurchaserEmail) {
      setCheckoutError('Please enter an email so Victoria\'s Place can send your ticket receipt.');
      return;
    }

    setIsPreparing(true);
    setCheckoutError('');

    try {
      const paymentConfig = await getSitePaymentConfig();
      if (!paymentConfig.publishable_key) {
        throw new Error('Stripe publishable key is not configured for this project.');
      }
      if (!paymentConfig.stripe_connect_account_configured) {
        throw new Error('Stripe connected account is not configured for this project.');
      }

      const intent = await createEventTicketIntent({
        eventId: event.id,
        ticketId: ticket.id,
        quantity: ticketQuantity,
        purchaserName: effectivePurchaserName,
        purchaserEmail: effectivePurchaserEmail,
        vendorProfileType: vendorRegistration?.profile_type,
        vendorBusinessName: vendorRegistration?.business_name,
        vendorContactName: vendorRegistration?.contact_name,
        vendorEmail: vendorRegistration?.email,
        vendorPhone: vendorRegistration?.phone,
        vendorWebsiteUrl: vendorRegistration?.website_url,
        vendorInstagramHandle: vendorRegistration?.instagram_handle,
        vendorDescription: vendorRegistration?.description,
        vendorBoothDetails: vendorRegistration?.booth_details,
      });

      if (!intent.client_secret) {
        throw new Error('Stripe did not return a client secret for this ticket.');
      }

      setStripePromise(
        loadStripe(
          paymentConfig.publishable_key,
          intent.stripe_account_id ? { stripeAccount: intent.stripe_account_id } : undefined,
        ),
      );
      setClientSecret(intent.client_secret);
      setIntentSummary(intent);
    } catch (error) {
      setCheckoutError(error instanceof Error ? error.message : 'The ticket checkout could not be prepared right now.');
    } finally {
      setIsPreparing(false);
    }
  }

  async function handleSubmitAdultModelApplication(eventInput: FormEvent<HTMLFormElement>) {
    eventInput.preventDefault();
    if (!adultModelForm || !adultModelReady) {
      setAdultModelError('Complete the required fields before submitting the adult model application.');
      return;
    }

    setIsSubmittingAdultModel(true);
    setAdultModelError('');
    setAdultModelSuccess('');

    try {
      const application = await submitPublicAdultModelApplication({
        eventId: event.id,
        ticketId: ticket.id,
        displayName: adultModelForm.display_name,
        email: adultModelForm.email,
        phone: adultModelForm.phone,
        birthday: adultModelForm.birthday,
        city: adultModelForm.city,
        state: adultModelForm.state,
        height: adultModelForm.height,
        instagramHandle: adultModelForm.instagram_handle,
        experienceSummary: adultModelForm.experience_summary,
        applicationMessage: adultModelForm.application_message,
        availabilityDetails: adultModelForm.availability_details,
      });

      setAdultModelApplication(application);
      setAdultModelSuccess('Adult model application submitted. Victoria\'s Place can now review it from the portal.');
    } catch (error) {
      setAdultModelError(error instanceof Error ? error.message : 'Unable to submit the adult model application right now.');
    } finally {
      setIsSubmittingAdultModel(false);
    }
  }

  async function handleSubmitChildModelApplication(eventInput: FormEvent<HTMLFormElement>) {
    eventInput.preventDefault();
    if (!childModelForm || !childModelReady) {
      setChildModelError('Complete the required fields before submitting the child model application.');
      return;
    }

    setIsSubmittingChildModel(true);
    setChildModelError('');
    setChildModelSuccess('');

    try {
      const application = await submitPublicChildModelApplication({
        eventId: event.id,
        ticketId: ticket.id,
        childFirstName: childModelForm.child_first_name,
        childLastName: childModelForm.child_last_name,
        childBirthday: childModelForm.child_birthday,
        guardianName: childModelForm.guardian_name,
        guardianEmail: childModelForm.guardian_email,
        guardianPhone: childModelForm.guardian_phone,
        guardianRelationship: childModelForm.guardian_relationship,
        city: childModelForm.city,
        state: childModelForm.state,
        instagramHandle: childModelForm.instagram_handle,
        experienceSummary: childModelForm.experience_summary,
        allergiesOrAccommodations: childModelForm.allergies_or_accommodations,
        applicationMessage: childModelForm.application_message,
        availabilityDetails: childModelForm.availability_details,
        participationConsent: childModelForm.participation_consent,
        mediaReleaseApproved: childModelForm.media_release_approved,
      });

      setChildModelApplication(application);
      setChildModelSuccess('Child model application submitted. Victoria\'s Place can now review it from the portal.');
    } catch (error) {
      setChildModelError(error instanceof Error ? error.message : 'Unable to submit the child model application right now.');
    } finally {
      setIsSubmittingChildModel(false);
    }
  }

  return (
    <section className="event-checkout-panel">
      <div className="event-checkout-heading">
        <div>
          <p className="home-section-label">Ticket checkout</p>
          <h3>{ticket.name}</h3>
        </div>
        <div className="event-checkout-price">
          <span>{adultModelTicket || childModelTicket ? 'Application' : 'Total charge'}</span>
          <strong>{adultModelTicket || childModelTicket ? 'No fee' : formatUsdCents(totalCents)}</strong>
        </div>
      </div>

      <div className="event-checkout-grid">
        <div className="event-checkout-summary">
          {adultModelTicket ? (
            <>
              <p>
                Adult model registration is handled as a public application from this event page. Fill out the form
                below and Victoria&apos;s Place can review it in the portal without requiring an account.
              </p>
              <dl className="event-checkout-breakdown">
                <div className="event-checkout-breakdown-row">
                  <dt>{ticket.name}</dt>
                  <dd>No fee</dd>
                </div>
                <div className="event-checkout-breakdown-row event-checkout-breakdown-row--total">
                  <dt>Application status</dt>
                  <dd>{adultModelApplication ? adultModelApplication.status.replace('-', ' ') : 'Ready to submit'}</dd>
                </div>
              </dl>
            </>
          ) : childModelTicket ? (
            <>
              <p>
                Child model registration is handled as a guardian-led application from this event page. Fill out the
                form below and Victoria&apos;s Place can review it in the portal without requiring an account.
              </p>
              <dl className="event-checkout-breakdown">
                <div className="event-checkout-breakdown-row">
                  <dt>{ticket.name}</dt>
                  <dd>No fee</dd>
                </div>
                <div className="event-checkout-breakdown-row event-checkout-breakdown-row--total">
                  <dt>Application status</dt>
                  <dd>{childModelApplication ? childModelApplication.status.replace('-', ' ') : 'Ready to submit'}</dd>
                </div>
              </dl>
            </>
          ) : (
            <>
              <p>
                Purchase this ticket directly from the event page. Victoria&apos;s Place receives the full ticket amount,
                and the processing fee is shown upfront.
              </p>
              <dl className="event-checkout-breakdown">
                <div className="event-checkout-breakdown-row">
                  <dt>Price per ticket</dt>
                  <dd>{formatUsdCents(unitAmountCents)}</dd>
                </div>
                <div className="event-checkout-breakdown-row">
                  <dt>Quantity</dt>
                  <dd>{ticketQuantity}</dd>
                </div>
                <div className="event-checkout-breakdown-row">
                  <dt>{ticket.name} subtotal</dt>
                  <dd>{formatUsdCents(baseAmountCents)}</dd>
                </div>
                <div className="event-checkout-breakdown-row">
                  <dt>Processing fee</dt>
                  <dd>{formatUsdCents(customerFeeCents)}</dd>
                </div>
                <div className="event-checkout-breakdown-row event-checkout-breakdown-row--total">
                  <dt>Total charge</dt>
                  <dd>{formatUsdCents(totalCents)}</dd>
                </div>
              </dl>
              <p className="event-checkout-note">
                Processing is added to the ticket total so the connected Victoria&apos;s Place account receives the base
                ticket amount.
              </p>
            </>
          )}
        </div>

        <div className="event-checkout-form-shell">
          {vendorTicket ? (
        <VendorCheckoutSection
          event={event}
          ticket={ticket}
          ticketQuantity={ticketQuantity}
          totalCents={totalCents}
          isPreparing={isPreparing}
          checkoutError={checkoutError}
          clientSecret={clientSecret}
          stripePromise={stripePromise}
          intentSummary={intentSummary}
          vendorRegistration={vendorRegistration}
          vendorRegistrationReady={vendorRegistrationReady}
          setVendorRegistration={setVendorRegistration}
          setTicketQuantity={setTicketQuantity}
          handlePrepareCheckout={handlePrepareCheckout}
          handlePaymentComplete={handleTicketPaymentComplete}
        />
          ) : adultModelTicket ? (
            <AdultModelSection
              adultModelForm={adultModelForm}
              adultModelApplication={adultModelApplication}
              adultModelError={adultModelError}
              adultModelSuccess={adultModelSuccess}
              isSubmittingAdultModel={isSubmittingAdultModel}
              adultModelReady={adultModelReady}
              setAdultModelForm={setAdultModelForm}
              handleSubmitAdultModelApplication={handleSubmitAdultModelApplication}
            />
          ) : childModelTicket ? (
            <ChildModelSection
              childModelForm={childModelForm}
              childModelApplication={childModelApplication}
              childModelError={childModelError}
              childModelSuccess={childModelSuccess}
              isSubmittingChildModel={isSubmittingChildModel}
              childModelReady={childModelReady}
              setChildModelForm={setChildModelForm}
              handleSubmitChildModelApplication={handleSubmitChildModelApplication}
            />
          ) : (
        <GeneralTicketSection
          event={event}
          ticket={ticket}
          ticketQuantity={ticketQuantity}
          totalCents={totalCents}
          purchaserName={purchaserName}
          purchaserEmail={purchaserEmail}
          isPreparing={isPreparing}
          checkoutError={checkoutError}
          clientSecret={clientSecret}
          stripePromise={stripePromise}
          intentSummary={intentSummary}
          setPurchaserName={setPurchaserName}
          setPurchaserEmail={setPurchaserEmail}
          setTicketQuantity={setTicketQuantity}
          handlePrepareCheckout={handlePrepareCheckout}
          handlePaymentComplete={handleTicketPaymentComplete}
        />
          )}
        </div>
      </div>
    </section>
  );
}
